import axios from 'axios';

// Adresse du backend. Vide = la même origine que la page, ce qui est le cas en
// production (le serveur sert l'app et l'API) et INDISPENSABLE depuis un
// téléphone : « localhost » y désignerait le téléphone lui-même, pas le serveur.
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
  withCredentials: true, // le cookie de session voyage avec chaque appel
});

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
// Session expirée ou fermée ailleurs : l'app repasse par l'écran de connexion au
// lieu d'afficher une erreur incompréhensible sur chaque écran.
let onSessionPerdue = null;
export const surSessionPerdue = (rappel) => { onSessionPerdue = rappel; };

api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - Status: ${response.status}`);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/api/auth/') && onSessionPerdue) {
      onSessionPerdue();
    }
    return Promise.reject(error);
  }
);

// Connexion et comptes
export const authAPI = {
  // Ces deux appels sont les tout premiers de l'app : sur un téléphone qui vient
  // de rétablir sa connexion, dix secondes sont vite atteintes.
  who: async () => (await api.get('/api/auth/who', { timeout: 20000 })).data,
  me: async () => (await api.get('/api/auth/me', { timeout: 20000 })).data,
  login: async (userId, code) =>
    (await api.post('/api/auth/login', { userId, code }, { timeout: 30000 })).data,
  logout: async () => (await api.post('/api/auth/logout')).data,
  listAccounts: async () => (await api.get('/api/auth/users')).data,
  createAccount: async (payload) =>
    (await api.post('/api/auth/users', payload, { timeout: 30000 })).data,
  updateAccount: async (id, payload) =>
    (await api.put(`/api/auth/users/${id}`, payload, { timeout: 30000 })).data,
};

// Écran de terrain : chantiers en cours et équipe, sans aucun montant
export const mobileAPI = {
  context: async () => (await api.get('/api/mobile/context', { timeout: 20000 })).data,
};

// Employee API functions
export const employeeAPI = {
  // Get all employees
  getAll: async () => {
    const response = await api.get('/api/employees');
    return response.data;
  },

  // Get single employee
  getById: async (id) => {
    const response = await api.get(`/api/employees/${id}`);
    return response.data;
  },

  // Create new employee
  create: async (employeeData) => {
    const response = await api.post('/api/employees', employeeData);
    return response.data;
  },

  // Update employee
  update: async (id, employeeData) => {
    const response = await api.put(`/api/employees/${id}`, employeeData);
    return response.data;
  },

  // Delete employee
  // « Supprimer » archive : la fiche et l'historique d'heures restent.
  delete: async (id) => {
    const response = await api.delete(`/api/employees/${id}`);
    return response.data;
  },

  restore: async (id) => {
    const response = await api.post(`/api/employees/${id}/restore`);
    return response.data;
  },

  // Statistiques par salarié : valeur produite, coût réel, résultat net
  stats: async () => {
    const response = await api.get('/api/employees/stats', { timeout: 120000 });
    return response.data;
  },
  refreshPayroll: async () => {
    const response = await api.post('/api/employees/stats/payroll', {}, { timeout: 120000 });
    return response.data;
  },
  // Sur le serveur hébergé, la relecture est faite par l'agent du PC : on suit son avancement
  payrollStatus: async () => {
    const response = await api.get('/api/employees/stats/payroll/status');
    return response.data;
  },

  // Déclare une absence (congé/maladie/autre) pour PLUSIEURS salariés en une fois :
  // une seule réorganisation du planning côté serveur. motif requis pour type "autre".
  addBulkAbsence: async (type, assignments, motif) => {
    const response = await api.post('/api/employees/bulk-absence', { type, assignments, motif },
      { timeout: 120000 });
    return response.data;
  },

  // Déclaration des heures au comptable (absences+motif, formations, zones de
  // trajet, paniers) : calcul brut pour relecture, puis génération du fichier
  // à partir de CE QUI A ÉTÉ RELU (potentiellement corrigé), pas un recalcul.
  payrollExportPreview: async (month) => {
    const response = await api.get('/api/employees/payroll-export/preview', {
      params: { month }, timeout: 60000,
    });
    return response.data;
  },
  payrollExportCsv: async (declaration) => {
    const response = await api.post('/api/employees/payroll-export/csv', declaration,
      { responseType: 'blob', timeout: 60000 });
    return response.data;
  },
  payrollExportPdf: async (declaration) => {
    const response = await api.post('/api/employees/payroll-export/pdf', declaration,
      { responseType: 'blob', timeout: 60000 });
    return response.data;
  },

};

// État de la sauvegarde nocturne et du disque du serveur (bandeau d'alerte)
export const systemAPI = {
  backupStatus: async () => {
    const response = await api.get('/api/backup/status', { timeout: 15000 });
    return response.data;
  },
};

// Paramètres de l'entreprise (prix de vente MO, coûts, seuils)
export const settingsAPI = {
  get: async () => {
    const response = await api.get('/api/settings');
    return response.data;
  },
  update: async (settings) => {
    const response = await api.put('/api/settings', settings);
    return response.data;
  },
};

// Achats : factures fournisseurs importées de Pennylane, détaillées par BL
export const purchasesAPI = {
  list: async () => {
    const response = await api.get('/api/purchases');
    return response.data;
  },
  context: async () => {
    const response = await api.get('/api/purchases/context');
    return response.data;
  },
  summary: async () => {
    const response = await api.get('/api/purchases/summary');
    return response.data;
  },
  // Le premier import télécharge aussi les PDF : plusieurs minutes possibles
  importFromPennylane: async () => {
    const response = await api.post('/api/purchases/import-pennylane', {}, { timeout: 600000 });
    return response.data;
  },
  saveLines: async (purchaseId, lines) => {
    const response = await api.put(`/api/purchases/${purchaseId}/lines`, { lines });
    return response.data;
  },
  setCategory: async (purchaseId, category) => {
    const response = await api.put(`/api/purchases/${purchaseId}`, { category });
    return response.data;
  },
  // Lecture automatique des BL d'après le PDF : une PROPOSITION rangée sur la facture
  // (suggestedLines), qui ne devient le détail qu'une fois enregistrée dans l'éditeur.
  autoRead: async (purchaseId) => {
    const response = await api.post(`/api/purchases/${purchaseId}/auto-read`, {}, { timeout: 300000 });
    return response.data;
  },
  autoReadAll: async () => {
    const response = await api.post('/api/purchases/auto-read');
    return response.data;
  },
  autoReadStatus: async () => {
    const response = await api.get('/api/purchases/auto-read/status');
    return response.data;
  },
  // Alerte « présente plusieurs fois dans Pennylane » : marquée vue, elle sort des listes.
  acknowledgeWarning: async (purchaseId) => {
    const response = await api.post(`/api/purchases/${purchaseId}/acknowledge-warning`);
    return response.data;
  },
  setSupplierCategory: async (supplierKey, category) => {
    const response = await api.put(`/api/purchase-suppliers/${encodeURIComponent(supplierKey)}`, { category });
    return response.data;
  },
  fileUrl: (purchaseId) => `${API_BASE_URL || ''}/api/purchases/${purchaseId}/file`,
  pageCount: async (purchaseId) => {
    const response = await api.get(`/api/purchases/${purchaseId}/pages`);
    return response.data;
  },
  // Page de la facture rendue en image par le serveur, à la largeur demandée
  pageImageUrl: (purchaseId, page, width) => `${API_BASE_URL || ''}/api/purchases/${purchaseId}/pages/${page}?width=${width}`,
};

// Pennylane API functions
export const pennylaneAPI = {
  // List accepted quotes not yet imported as chantiers.
  // Timeout plus long que la valeur par défaut (10s) : chaque devis nécessite un
  // appel Pennylane séparé pour récupérer les infos du client associé.
  fetchPendingQuotes: async () => {
    const response = await api.post('/api/sync/quotes-from-pennylane', {}, { timeout: 30000 });
    return response.data;
  },
};

// Project API functions
export const projectAPI = {
  // Get all projects
  getAll: async () => {
    const response = await api.get('/api/projects');
    return response.data;
  },

  // Get single project
  getById: async (id) => {
    const response = await api.get(`/api/projects/${id}`);
    return response.data;
  },

  // Create new project
  create: async (projectData) => {
    const response = await api.post('/api/projects', projectData);
    return response.data;
  },

  // Update project
  update: async (id, projectData) => {
    const response = await api.put(`/api/projects/${id}`, projectData);
    return response.data;
  },

  // Delete project
  delete: async (id) => {
    const response = await api.delete(`/api/projects/${id}`);
    return response.data;
  },

  // Mark project as completed
  markCompleted: async (id) => {
    const response = await api.put(`/api/projects/${id}/complete`);
    return response.data;
  },

  // Met un chantier en pause : démobilise son équipe jusqu'à la reprise
  pause: async (id) => {
    const response = await api.put(`/api/projects/${id}/pause`, {}, { timeout: 60000 });
    return response.data;
  },

  // Reprend un chantier en pause : replace l'équipe démobilisée si possible
  resume: async (id) => {
    const response = await api.put(`/api/projects/${id}/resume`, {}, { timeout: 60000 });
    return response.data;
  },

  // Heures prévues déduites du devis, via le prix de vente MO des paramètres
  calculateHoursFromQuote: async (quoteAmountHT, purchasesAmountHT) => {
    const response = await api.post('/api/projects/calculate-hours-from-quote', {
      quoteAmountHT,
      purchasesAmountHT
    });
    return response.data;
  },

  // Calculate project duration
  calculateDuration: async (projectData) => {
    const response = await api.post('/api/projects/calculate-duration', {
      ...projectData,
      calculateInDays: true  // Always calculate precise days
    });
    return response.data;
  },

  // Auto-assign employees to project
  autoAssign: async (projectData) => {
    const response = await api.post('/api/projects/auto-assign', projectData);
    return response.data;
  },

  // Get next available date
  getNextAvailableDate: async () => {
    const response = await api.get('/api/projects/next-available-date');
    return response.data;
  },

  // Move project with conflict resolution
  moveWithConflictResolution: async (projectId, newStartDate) => {
    const response = await api.post('/api/projects/move-with-conflict-resolution', {
      projectId,
      newStartDate
    });
    return response.data;
  },

  // Find optimal slot for new project
  findOptimalSlot: async (projectData) => {
    const response = await api.post('/api/projects/find-optimal-slot', projectData);
    return response.data;
  },

  // Choix de couleur d'un chantier
  getColorChoices: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/color-choices`);
    return response.data;
  },
  addColorChoice: async (projectId, choice) => {
    const response = await api.post(`/api/projects/${projectId}/color-choices`, choice);
    return response.data;
  },
  deleteColorChoice: async (projectId, choiceId) => {
    const response = await api.delete(`/api/projects/${projectId}/color-choices/${choiceId}`);
    return response.data;
  },
  // Fiche de choix de couleurs en PDF : l'adresse sert à l'ouvrir (impression),
  // le contenu à la télécharger sous son nom.
  colorChoicesPdfUrl: (projectId) => `${API_BASE_URL}/api/projects/${projectId}/color-choices/pdf`,
  colorChoicesPdf: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/color-choices/pdf`,
                                   { responseType: 'blob', timeout: 30000 });
    return response.data;
  },

  // Documents d'un chantier
  getDocuments: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/documents`);
    return response.data;
  },
  addDocument: async (projectId, doc) => {
    const response = await api.post(`/api/projects/${projectId}/documents`, doc);
    return response.data;
  },
  // Dépose un fichier hébergé par l'app au lieu de coller un lien existant.
  // `doc` = { file, type, name, comment }.
  uploadDocument: async (projectId, doc) => {
    const formData = new FormData();
    formData.append('file', doc.file);
    if (doc.type) formData.append('type', doc.type);
    if (doc.name) formData.append('name', doc.name);
    if (doc.comment) formData.append('comment', doc.comment);
    const response = await api.post(`/api/projects/${projectId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  deleteDocument: async (projectId, documentId) => {
    const response = await api.delete(`/api/projects/${projectId}/documents/${documentId}`);
    return response.data;
  },

  // Première disponibilité pour un nouveau chantier : sans rien déplacer
  // (strict) et en recalant seulement les chantiers souples (flexible)
  firstAvailability: async ({ hours = 0, minEmployees = 1, isComplex = false } = {}) => {
    const response = await api.get('/api/planning/first-availability', {
      params: { hours, minEmployees, isComplex }
    });
    return response.data;
  },

  // Bilan des chantiers, calculé par l'app : écart d'heures, marge réelle
  planningFinancials: async () => {
    const response = await api.get('/api/planning/financials', { timeout: 30000 });
    return response.data;
  },

  // Factures clients sans devis Pennylane, à rattacher à la main à un chantier terminé :
  // leur montant remplace le devis dans le calcul de marge
  availableInvoices: async () => {
    const response = await api.get('/api/finance/customer-invoices/available', { timeout: 30000 });
    return response.data;
  },

  // Date jusqu'à laquelle les heures vendues occupent toute l'équipe (bandeau du haut)
  soldUntil: async () => {
    const response = await api.get('/api/planning/sold-until', { timeout: 30000 });
    return response.data;
  },

  // Charge mois par mois : capacité des salariés, travail réel, heures libres
  planningLoad: async (months = 6) => {
    const response = await api.get('/api/planning/load', { params: { months }, timeout: 30000 });
    return response.data;
  },

  // Reorganize entire planning automatically
  reorganizePlanning: async () => {
    const response = await api.post('/api/projects/reorganize-planning');
    return response.data;
  },

  // Daily consistency check: delay not-yet-started projects + fix double-booked employees
  dailyReconciliation: async () => {
    const response = await api.post('/api/projects/daily-reconciliation');
    return response.data;
  },

  // Pointage d'heures
  addTimeEntries: async (batch) => {
    const response = await api.post('/api/time-entries', batch, { timeout: 60000 });
    return response.data;
  },
};

// Pointages de l'app : consulter, corriger, supprimer
export const timeEntryAPI = {
  list: async ({ since, until } = {}) => {
    const response = await api.get('/api/time-entries', { params: { since, until }, timeout: 30000 });
    return response.data;
  },
  update: async (id, entry) => {
    const response = await api.put(`/api/time-entries/${id}`, entry, { timeout: 60000 });
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/api/time-entries/${id}`, { timeout: 60000 });
    return response.data;
  },
};

// Format a Date using its local (not UTC) calendar day, avoiding the
// off-by-one shift that date.toISOString() introduces for timezones ahead of UTC.
export const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility functions for data transformation
export const dataTransformers = {
  // Convert API date strings to Date objects for frontend use
  transformEmployee: (employee) => {
    if (!employee) return employee;
    
    return {
      ...employee,
      specificLeaveDates: employee.specificLeaveDates?.map(dateStr =>
        typeof dateStr === 'string' ? new Date(dateStr) : dateStr
      ) || [],
      sickLeaveDates: employee.sickLeaveDates?.map(dateStr =>
        typeof dateStr === 'string' ? new Date(dateStr) : dateStr
      ) || [],
      trainingWeeks: employee.trainingWeeks?.map(week => ({
        ...week,
        start: typeof week.start === 'string' ? new Date(week.start) : week.start,
        end: typeof week.end === 'string' ? new Date(week.end) : week.end,
      })) || [],
    };
  },

  // Transform employee data for API submission
  prepareEmployeeForAPI: (employee, isUpdate = false) => {
    let employeeData = { ...employee };
    
    // For creation, remove fields that backend will generate/manage
    if (!isUpdate) {
      const { id, fullName, currentAssignments, status, ...cleanData } = employeeData;
      employeeData = cleanData;
    }
    
    return {
      ...employeeData,
      // Ensure numeric fields are numbers, not strings
      weeklyHours: typeof employeeData.weeklyHours === 'string' ? 
        parseFloat(employeeData.weeklyHours) : employeeData.weeklyHours,
      annualLeave: typeof employeeData.annualLeave === 'string' ? 
        parseInt(employeeData.annualLeave) : employeeData.annualLeave,
      annualSellableHours: typeof employeeData.annualSellableHours === 'string' ? 
        parseFloat(employeeData.annualSellableHours) : employeeData.annualSellableHours,
      productivityCoefficient: typeof employeeData.productivityCoefficient === 'string' ?
        parseFloat(employeeData.productivityCoefficient) : (employeeData.productivityCoefficient ?? 1),
      specificLeaveDates: employee.specificLeaveDates?.map(date =>
        date instanceof Date ? toLocalDateString(date) : date
      ) || [],
      sickLeaveDates: employee.sickLeaveDates?.map(date =>
        date instanceof Date ? toLocalDateString(date) : date
      ) || [],
      trainingWeeks: employee.trainingWeeks?.map(week => ({
        ...week,
        start: week.start instanceof Date ? toLocalDateString(week.start) : week.start,
        end: week.end instanceof Date ? toLocalDateString(week.end) : week.end,
        weekNumber: typeof week.weekNumber === 'string' ?
          parseInt(week.weekNumber) : week.weekNumber,
      })) || [],
    };
  },

  // Transform project for consistent format
  transformProject: (project) => {
    if (!project) return project;
    
    return {
      ...project,
      // Ensure assignedEmployees is always an array of strings
      assignedEmployees: project.assignedEmployees?.map(id => String(id)) || [],
    };
  },

  // Prepare project data for API submission
  prepareProjectForAPI: (project, isUpdate = false) => {
    let projectData = { ...project };
    
    // For creation, remove id that backend will generate
    if (!isUpdate) {
      const { id, ...cleanData } = projectData;
      projectData = cleanData;
    }
    
    return {
      ...projectData,
      // Ensure numeric fields are numbers, not strings
      hoursToSell: typeof projectData.hoursToSell === 'string' ? 
        parseInt(projectData.hoursToSell) : projectData.hoursToSell,
      minEmployees: typeof projectData.minEmployees === 'string' ? 
        parseInt(projectData.minEmployees) : projectData.minEmployees,
      duration: typeof projectData.duration === 'string' ? 
        parseInt(projectData.duration) : projectData.duration,
      assignedEmployees: project.assignedEmployees?.map(id => String(id)) || [],
    };
  },
};

// Error handling helper
export const handleAPIError = (error, defaultMessage = 'An error occurred') => {
  if (error.response) {
    // Server responded with error status
    return error.response.data?.detail || error.response.data?.message || defaultMessage;
  } else if (error.request) {
    // Request was made but no response received
    return 'Unable to connect to server. Please check your connection.';
  } else {
    // Something else happened
    return error.message || defaultMessage;
  }
};

export default api;